import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RiDeleteBinFill, RiPencilLine } from "react-icons/ri";
import { deleteSystemListItem, fetchSystemList } from "../../../Redux/Thunks/systemListsThunk";
import { selectSystemListStateForType } from "../../../Redux/Slices/systemListsSlice";
import Typography from "../../../Utility/Typography";
import Button from "../../../Utility/Button/Button";
import AddButton from "../../../Utility/AddButton/AddButton";
import FloatingTooltip from "../../../Utility/FloatingTooltip/FloatingTooltip";
import { ContentLoader } from "../../../Utility/Loader";
import { useSorting, SortableHeader } from "../../../Utility/Sorting";
import SystemListTagModal from "./SystemListTagModal";
import DeleteConfirmModal from "../../../Utility/DeleteConfirmModal/DeleteConfirmModal";
import PermissionDeniedModal from "../../../Utility/PermissionDeniedModal/PermissionDeniedModal";
import { showError, showSuccess } from "../../../utils/toast";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSION_KEYS } from "../../../permissions/permissionKeys";
import "../../Clients/clients.css";

function isRowActive(row) {
  const v = row?.isActive;
  return v === true || v === 1 || v === "1";
}

function statusBadgeClass(active) {
  return active ? "bg-badge-success" : "bg-badge-secondary";
}

/**
 * Client/vendor tag rows from GET `/system-lists`. List + sort are always available.
 * Add uses `SYSTEM_LIST_ADD`; the actions column appears only when the user can edit or delete.
 *
 * @param {{ listType: "CLIENT_TAG" | "VENDOR_TAG"; panelTitle: string; entityLabel: string }} props
 */
export default function SystemListTagPanel({ listType, panelTitle, entityLabel }) {
  const dispatch = useDispatch();
  const { can } = usePermissions();
  const canAdd = can(PERMISSION_KEYS.SYSTEM_LIST_ADD);
  const canEdit = can(PERMISSION_KEYS.SYSTEM_LIST_EDIT);
  const canDelete = can(PERMISSION_KEYS.SYSTEM_LIST_DELETE);
  const showActionsColumn = canEdit || canDelete;
  const colCount = showActionsColumn ? 3 : 2;

  const listState = useSelector((s) => selectSystemListStateForType(s, listType));
  const loading = listState?.status === "loading";
  const error = listState?.error ?? null;

  const [modalOpen, setModalOpen] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deniedPermissionKey, setDeniedPermissionKey] = useState(null);

  const fetchContext = `settings-system-lists-${listType}`;

  const load = useCallback(() => {
    void dispatch(fetchSystemList({ listType, context: fetchContext, force: true }));
  }, [dispatch, listType, fetchContext]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    const rawList = Array.isArray(listState?.list) ? listState.list : [];
    return rawList.map((r) => ({
      ...r,
      status: isRowActive(r) ? "Active" : "Inactive",
    }));
  }, [listState?.list]);

  const nextSortOrder = useMemo(() => {
    const max = rows.reduce((m, r) => {
      const n = Number(r.sortOrder);
      return Number.isFinite(n) ? Math.max(m, n) : m;
    }, -1);
    return max + 1;
  }, [rows]);

  const { sortedData, sortKey, sortDirection, handleSort } = useSorting(rows, {
    defaultSortKey: "name",
    defaultSortDirection: "asc",
  });

  const openAdd = () => {
    setItemToEdit(null);
    setModalOpen(true);
  };

  const requestAdd = () => {
    if (!canAdd) {
      setDeniedPermissionKey(PERMISSION_KEYS.SYSTEM_LIST_ADD);
      return;
    }
    openAdd();
  };

  const openEdit = (row) => {
    setItemToEdit(row);
    setModalOpen(true);
  };

  const requestEdit = (row) => {
    if (!canEdit) {
      setDeniedPermissionKey(PERMISSION_KEYS.SYSTEM_LIST_EDIT);
      return;
    }
    openEdit(row);
  };

  const requestDelete = (row) => {
    if (!canDelete) {
      setDeniedPermissionKey(PERMISSION_KEYS.SYSTEM_LIST_DELETE);
      return;
    }
    setItemToDelete(row);
  };

  const closeModal = () => {
    setModalOpen(false);
    setItemToEdit(null);
  };

  const handleSaved = () => {
    load();
  };

  const handleConfirmDelete = async () => {
    if (!canDelete) return;
    const id = itemToDelete?.id;
    if (!id) return;
    setDeleteSubmitting(true);
    try {
      await dispatch(deleteSystemListItem(String(id))).unwrap();
      showSuccess(`${entityLabel} deleted.`);
      setItemToDelete(null);
      await dispatch(fetchSystemList({ listType, context: fetchContext, force: true })).unwrap();
    } catch (e) {
      showError(getApiErrorMessage(e, `Failed to delete ${entityLabel.toLowerCase()}.`));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
        {error}
      </div>
    );
  }

  return (
    <div className="clients-page-content !bg-transparent">
      <ContentLoader loading={loading} className="min-h-[calc(100vh-14rem)]">
        <div className="flex w-full flex-col items-stretch justify-between gap-2 py-3 sm:flex-row sm:items-center">
          <Typography variant="text1" className="text-xl font-semibold text-text-primary">
            {panelTitle}
          </Typography>
          {canAdd ? (
            <AddButton onClick={requestAdd} tooltip={`Add ${entityLabel.toLowerCase()}`} />
          ) : null}
        </div>

        <div className="w-full">
          <div className="relative flex min-w-0 flex-col rounded-md bg-surface-primary shadow-sm transition-shadow">
            <div className="flex-1 p-2">
              <div className="overflow-x-auto">
                <table className="clients-table mb-0 w-full border-collapse">
                  <thead className="bg-surface-secondary">
                    <tr>
                      <SortableHeader
                        columnKey="name"
                        label="Name"
                        onSort={handleSort}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                      />
                      <SortableHeader
                        columnKey="status"
                        label="Status"
                        onSort={handleSort}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                      />
                      {showActionsColumn ? (
                        <SortableHeader
                          columnKey="action"
                          label="Actions"
                          onSort={handleSort}
                          currentSortKey={sortKey}
                          sortDirection={sortDirection}
                          sortable={false}
                          className="action-col"
                        />
                      ) : null}
                    </tr>
                  </thead>
                  <tbody className="relative">
                    {loading ? (
                      <tr aria-hidden="true">
                        <td colSpan={colCount} className="h-60 min-h-[240px] border-0 p-0" />
                      </tr>
                    ) : sortedData.length === 0 ? (
                      <tr>
                        <td colSpan={colCount} className="py-6 text-center">
                          <Typography className="text-text-secondary">
                            {canAdd
                              ? `No ${panelTitle.toLowerCase()} found. Add one to get started.`
                              : `No ${panelTitle.toLowerCase()} found.`}
                          </Typography>
                        </td>
                      </tr>
                    ) : (
                      sortedData.map((row) => {
                        const active = isRowActive(row);
                        return (
                          <tr key={row.id ?? row.name}>
                            <td className="align-middle text-start">
                              {canEdit ? (
                                <button
                                  type="button"
                                  className="m-0 w-full cursor-pointer border-0 bg-transparent p-0 text-left"
                                  onClick={() => requestEdit(row)}
                                  aria-label={`Edit ${row.name ?? entityLabel}`}
                                >
                                  <Typography
                                    variant="span1"
                                    className="font-medium text-text-primary"
                                  >
                                    {row.name ?? "—"}
                                  </Typography>
                                </button>
                              ) : (
                                <Typography
                                  variant="span1"
                                  className="font-medium text-text-primary"
                                >
                                  {row.name ?? "—"}
                                </Typography>
                              )}
                            </td>
                            <td className="align-middle text-start">
                              <Typography
                                variant="span1"
                                component="span"
                                className={`badge rounded-full px-3 py-1 text-xs font-bold text-white ${statusBadgeClass(active)}`}
                              >
                                {row.status}
                              </Typography>
                            </td>
                            {showActionsColumn ? (
                              <td className="content-cell align-middle text-start">
                                <div className="flex min-h-[28px] items-center justify-end gap-2">
                                  {canEdit ? (
                                    <FloatingTooltip label="Edit">
                                      <Button
                                        type="button"
                                        size="sm"
                                        aria-label={`Edit ${entityLabel}`}
                                        className="btn-warning-new !flex !h-7 !w-7 !items-center !justify-center !p-0 !font-normal !leading-none"
                                        onClick={() => requestEdit(row)}
                                      >
                                        <RiPencilLine className="text-[12px] text-white" />
                                      </Button>
                                    </FloatingTooltip>
                                  ) : null}
                                  {canDelete ? (
                                    <FloatingTooltip label="Delete">
                                      <Button
                                        type="button"
                                        size="sm"
                                        aria-label={`Delete ${entityLabel}`}
                                        className="btn-danger-new !flex !h-7 !w-7 !items-center !justify-center !p-0 !font-normal !leading-none"
                                        onClick={() => requestDelete(row)}
                                      >
                                        <RiDeleteBinFill className="text-[12px] text-white" />
                                      </Button>
                                    </FloatingTooltip>
                                  ) : null}
                                </div>
                              </td>
                            ) : null}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </ContentLoader>

      {modalOpen ? (
        <SystemListTagModal
          isOpen={modalOpen}
          onClose={closeModal}
          onSaved={handleSaved}
          listType={listType}
          entityLabel={entityLabel}
          existingItem={itemToEdit}
          nextSortOrder={nextSortOrder}
        />
      ) : null}

      <DeleteConfirmModal
        isOpen={!!itemToDelete}
        onClose={() => !deleteSubmitting && setItemToDelete(null)}
        onConfirm={() => void handleConfirmDelete()}
        title={`Delete ${entityLabel.toLowerCase()}`}
        itemName={itemToDelete ? String(itemToDelete.name ?? "") : ""}
        loading={deleteSubmitting}
      />

      <PermissionDeniedModal
        isOpen={deniedPermissionKey != null}
        onClose={() => setDeniedPermissionKey(null)}
        permissionKey={deniedPermissionKey}
      />
    </div>
  );
}
